<template>
  <div class="max-findings">
    <div v-if="title || (filterable && present.length > 1)" class="max-findings__bar">
      <h3 v-if="title" class="max-panel__title">{{ title }}</h3>
      <div v-if="filterable && present.length > 1" class="max-findings__filters" role="group" :aria-label="title || undefined">
        <button
          type="button"
          class="max-chip"
          :aria-pressed="active === null"
          @click="active = null"
        >
          {{ ctx.labels.all }} <span class="max-chip__count">{{ items.length }}</span>
        </button>
        <button
          v-for="sev in present"
          :key="sev"
          type="button"
          class="max-chip"
          :data-severity="sev"
          :aria-pressed="active === sev"
          @click="active = active === sev ? null : sev"
        >
          <span class="max-badge__dot" aria-hidden="true" />
          {{ ctx.labels.severity[sev] }} <span class="max-chip__count">{{ counts[sev] }}</span>
        </button>
      </div>
    </div>

    <p v-if="!items.length" class="max-text max-text--muted">{{ ctx.labels.noFindings }}</p>

    <div v-for="group in groups" :key="group.key" class="max-findings__group">
      <p v-if="groupBy === 'severity' && groups.length > 1" class="max-eyebrow" :data-severity="group.key">
        {{ ctx.labels.severity[group.key as Severity] }} · {{ group.items.length }}
      </p>
      <ul class="max-findings__list" :data-max-stagger="'true'">
        <li v-for="(item, i) in group.items" :key="(item.ruleId ?? '') + i" class="max-finding" :data-severity="item.severity">
          <span class="max-finding__marker" aria-hidden="true" />
          <div class="max-finding__head">
            <span class="max-badge" :data-severity="item.severity">{{ ctx.labels.severity[item.severity as Severity] }}</span>
            <h4 class="max-finding__title">{{ item.title }}</h4>
            <span v-if="item.count" class="max-badge">{{ item.count }}</span>
            <span v-if="item.impact" class="max-badge" data-intent="accent">{{ ctx.labels.impact[item.impact as 'high' | 'medium' | 'low'] }}</span>
            <span v-if="item.effort" class="max-badge">{{ ctx.labels.effort[item.effort as 'quick' | 'moderate' | 'involved'] }}</span>
          </div>
          <div class="max-finding__content">
            <p v-if="item.detail" class="max-finding__detail" style="white-space: pre-line">{{ item.detail }}</p>
            <div v-if="item.fix" class="max-finding__fix">
              <span class="max-finding__fix-label">{{ ctx.labels.fix }}</span>
              <span style="white-space: pre-line">{{ item.fix }}</span>
            </div>
            <p v-if="item.entity" class="max-finding__entity">
              {{ ctx.labels.affected }}:
              <button v-if="hostOpens(ctx, item.entity)" type="button" class="max-link" @click="ctx.openEntity!(item.entity)">{{ item.entity.label }}</button>
              <a v-else-if="safeUrl(item.entity.url)" :href="safeUrl(item.entity.url)!" target="_blank" rel="noopener noreferrer nofollow">{{ item.entity.label }}</a>
              <span v-else>{{ item.entity.label }}</span>
            </p>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { SEVERITIES, type Severity } from '../core/tokens.ts'
import { HTTPS_URL, type FindingItem } from '../core/document.ts'
import { useMaxContext, hostOpens } from '../runtime/context.ts'

const props = withDefaults(defineProps<{
  items: FindingItem[]
  groupBy?: 'severity' | 'none'
  title?: string
  filterable?: boolean
}>(), { groupBy: 'severity', title: undefined, filterable: true })

const ctx = useMaxContext()
const active = ref<Severity | null>(null)

const order = (sev: string) => SEVERITIES.indexOf(sev as Severity)
const sorted = computed(() => [...props.items].sort((a, b) => order(a.severity) - order(b.severity)))
const counts = computed(() => {
  const c: Record<string, number> = {}
  for (const item of props.items) c[item.severity] = (c[item.severity] ?? 0) + 1
  return c
})
const present = computed(() => SEVERITIES.filter(sev => counts.value[sev]))
const visible = computed(() => active.value ? sorted.value.filter(i => i.severity === active.value) : sorted.value)

const groups = computed(() => {
  if (props.groupBy !== 'severity') return [{ key: 'all', items: visible.value }]
  return SEVERITIES
    .map(sev => ({ key: sev as string, items: visible.value.filter(i => i.severity === sev) }))
    .filter(g => g.items.length)
})

const safeUrl = (url?: string) => (url && HTTPS_URL.test(url) ? url : null)
</script>
